import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Award,
  BookOpen,
  Briefcase,
  Clock,
  Filter,
  GraduationCap,
  Search,
  Star,
  Users,
} from 'lucide-react';
import EnrollmentModal from '../../components/enrollment/EnrollmentModal';
import { allCourses, courseDetails, type Course } from '../../data/courses';
import { getPublishedBundles, type Bundle } from '../../lib/commerce';
import { getPublishedCourses } from '../../lib/lms';

interface DisplayCourse extends Course {
  priceValue: number;
  isLms?: boolean;
}

const categoryLabels: Record<string, string> = {
  all: 'All Programs',
  Certificate: 'Certificate',
  Diploma: 'Diploma',
  'Advanced Diploma': 'Advanced Diploma',
  'Mini MBA': 'Mini MBA',
};

const categoryIcons: Record<string, React.ElementType> = {
  Certificate: BookOpen,
  Diploma: Award,
  'Advanced Diploma': GraduationCap,
  'Mini MBA': Briefcase,
};

const heroBgImage =
  'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80';

const fallbackImage = allCourses[0]?.image || '';

const priceValueFromLabel = (label: string) => Number(label.replace(/[^\d]/g, '')) || 0;

const formatPrice = (value: number) =>
  value === 0 ? 'Free' : `₦${value.toLocaleString('en-NG')}`;

const asDisplayCourse = (course: Course): DisplayCourse => {
  const detail = courseDetails[course.id];
  const priceValue = detail?.priceValue ?? priceValueFromLabel(course.price);
  return {
    ...course,
    price: formatPrice(priceValue),
    priceValue,
  };
};

const CoursesPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [catalogue, setCatalogue] = useState<DisplayCourse[]>(() => allCourses.map(asDisplayCourse));
  const [selectedCourse, setSelectedCourse] = useState<{
    id: string;
    title: string;
    price: string;
    priceValue: number;
  } | null>(null);
  const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bundles, setBundles] = useState<Bundle[]>([]);

  useEffect(() => {
    void getPublishedBundles().then(setBundles).catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;

    void getPublishedCourses()
      .then((rows) => {
        if (cancelled) return;

        const localIds = new Set(allCourses.map((course) => course.id));
        const dbById = new Map(rows.map((course) => [course.id, course]));

        const local = allCourses.map((course) => {
          const dbCourse = dbById.get(course.id);
          const detail = courseDetails[course.id];
          const priceValue =
            dbCourse?.price_value ?? detail?.priceValue ?? priceValueFromLabel(course.price);

          return {
            ...course,
            title: dbCourse?.title || course.title,
            category: dbCourse?.category || course.category,
            description: dbCourse?.tagline || dbCourse?.description || course.description,
            duration: dbCourse?.duration || course.duration,
            level: dbCourse?.level || course.level,
            price: formatPrice(priceValue),
            image: dbCourse?.thumbnail || course.image,
            priceValue,
          };
        });

        const extra: DisplayCourse[] = rows
          .filter((course) => !localIds.has(course.id) && course.category !== 'WhatsApp')
          .map((course) => ({
            id: course.id,
            title: course.title,
            category: course.category || 'Program',
            description: course.tagline || course.description || '',
            duration: course.duration || 'Self-paced',
            students: 'New cohort',
            level: course.level || 'All levels',
            rating: 5,
            price: formatPrice(course.price_value),
            image: course.thumbnail || fallbackImage,
            priceValue: course.price_value,
            isLms: true,
          }));

        setCatalogue([...local, ...extra]);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const categoryParam = params.get('category');
    if (categoryParam && categoryLabels[categoryParam]) {
      setSelectedCategory(categoryParam);
    }
  }, []);

  const categories = useMemo(() => {
    const present = Array.from(new Set(catalogue.map((course) => course.category)));
    const preferred = ['Certificate', 'Diploma', 'Advanced Diploma', 'Mini MBA'];
    return [
      'all',
      ...preferred.filter((category) => present.includes(category)),
      ...present.filter((category) => !preferred.includes(category)),
    ];
  }, [catalogue]);

  const filteredCourses = useMemo(() => {
    let availableCourses = [...catalogue];

    if (selectedCategory !== 'all') {
      availableCourses = availableCourses.filter((course) => course.category === selectedCategory);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      availableCourses = availableCourses.filter(
        (course) =>
          course.title.toLowerCase().includes(q) ||
          course.description.toLowerCase().includes(q) ||
          course.category.toLowerCase().includes(q),
      );
    }

    return availableCourses;
  }, [selectedCategory, searchQuery, catalogue]);

  const handleEnrollClick = (course: DisplayCourse) => {
    setSelectedBundle(null);
    setSelectedCourse({
      id: course.id,
      title: course.title,
      price: course.price,
      priceValue: course.priceValue,
    });
    setIsModalOpen(true);
  };

  const handleBundleClick = (bundle: Bundle) => {
    setSelectedCourse(null);
    setSelectedBundle(bundle);
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBgImage} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-800/75" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-32 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center rounded-full border border-blue-500/30 bg-blue-600/20 px-3 py-1 text-sm font-medium text-blue-300">
              <GraduationCap className="mr-2 h-4 w-4" />
              Export Management Programs
            </div>
            <h1 className="text-4xl font-bold leading-tight text-slate-50 sm:text-5xl lg:text-6xl">
              Build Your Career in{' '}
              <span className="text-blue-400">International Trade</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300 sm:text-xl">
              From foundational certificates to executive Mini MBA programs, our courses equip you with the
              practical skills and global network to thrive in export management.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#programs"
                className="inline-flex items-center rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-700"
              >
                Explore Programs
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center rounded-lg border border-slate-400 px-6 py-3 font-semibold text-slate-200 transition-colors hover:bg-slate-800/50"
              >
                Speak to an Advisor
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="block h-auto w-full">
            <path d="M0 80V40C240 0 480 0 720 20C960 40 1200 60 1440 40V80H0Z" fill="#f8fafc" />
          </svg>
        </div>
      </section>

      <section className="bg-slate-50 py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {[
              { label: 'Active Students', value: '4,000+', icon: Users },
              { label: 'Programs Offered', value: String(catalogue.length), icon: BookOpen },
              { label: 'Average Rating', value: '4.9/5', icon: Star },
              { label: 'Completion Rate', value: '96%', icon: Award },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-slate-100 bg-white p-6 text-center shadow-sm"
              >
                <stat.icon className="mx-auto mb-3 h-6 w-6 text-blue-600" />
                <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                <div className="mt-1 text-sm text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="programs" className="bg-slate-50 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">Our Programs</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">
              Choose a program that matches your career goals. Each pathway is designed to take you from
              foundational knowledge to executive leadership.
            </p>
          </div>

          <div className="mb-10 flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="relative w-full md:w-64">
              <Filter className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <select
                className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-slate-700 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                value={selectedCategory}
                onChange={(event) => setSelectedCategory(event.target.value)}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {categoryLabels[category] || category}
                  </option>
                ))}
              </select>
            </div>
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search programs..."
                className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-slate-700 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
          </div>

          {filteredCourses.length > 0 ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredCourses.map((course) => {
                const CatIcon = categoryIcons[course.category] || BookOpen;
                return (
                  <div
                    key={course.id}
                    className="flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:border-blue-200 hover:shadow-lg"
                  >
                    <div className="relative h-52 overflow-hidden">
                      {course.image ? (
                        <img
                          src={course.image}
                          alt={course.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                          onError={(event) => {
                            event.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-br from-blue-600 to-sky-400" />
                      )}
                      <div className="absolute left-4 top-4">
                        <span className="inline-flex items-center rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm backdrop-blur-sm">
                          <CatIcon className="mr-1.5 h-3.5 w-3.5 text-blue-600" />
                          {course.category}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <Link
                        to={`/course/${course.id}`}
                        className="mb-3 block text-xl font-bold text-slate-900 transition-colors hover:text-blue-600"
                      >
                        {course.title}
                      </Link>
                      <p className="mb-5 flex-1 text-sm leading-relaxed text-slate-600">{course.description}</p>

                      <div className="mb-5 flex items-center gap-4 text-sm text-slate-500">
                        <div className="flex items-center">
                          <Clock className="mr-1.5 h-4 w-4 text-slate-400" />
                          {course.duration}
                        </div>
                        <div className="flex items-center">
                          <Users className="mr-1.5 h-4 w-4 text-slate-400" />
                          {course.students}
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t border-slate-100 pt-5">
                        <div>
                          <div className="mb-0.5 text-xs text-slate-500">Tuition</div>
                          <div className="text-xl font-bold text-blue-600">{course.price}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center">
                            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                            <span className="ml-1 text-sm font-medium text-slate-700">{course.rating}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleEnrollClick(course)}
                            className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
                          >
                            Enroll
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-20 text-center">
              <BookOpen className="mx-auto mb-4 h-12 w-12 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-700">No programs found</h3>
              <p className="mt-1 text-slate-500">Try adjusting your search or filter criteria.</p>
            </div>
          )}
        </div>
      </section>

      {bundles.length > 0 && (
        <section className="bg-slate-50 pb-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-8 text-center">
              <h2 className="text-3xl font-bold text-slate-900">Grouped Pathways</h2>
              <p className="mx-auto mt-3 max-w-2xl text-lg text-slate-600">
                One payment enrolls you in every program in the bundle.
              </p>
            </div>
            <div className={`grid gap-6 ${bundles.length === 1 ? 'md:grid-cols-1' : 'md:grid-cols-2'}`}>
              {bundles.map((bundle) => (
                <div
                  key={bundle.id}
                  className="flex flex-col justify-between rounded-2xl border border-slate-100 bg-white p-6 shadow-sm"
                >
                  <div>
                    <div className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                      {bundle.course_ids.length} programs
                    </div>
                    <h3 className="mt-3 text-xl font-bold text-slate-900">{bundle.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{bundle.description}</p>
                  </div>
                  <div className="mt-6 flex items-end justify-between border-t border-slate-100 pt-5">
                    <div>
                      <div className="text-xs text-slate-500">Bundle tuition</div>
                      <div className="text-xl font-bold text-blue-600">
                        ₦{bundle.price_value.toLocaleString('en-NG')}
                      </div>
                      {bundle.compare_at_price > bundle.price_value && (
                        <div className="text-xs text-slate-400 line-through">
                          ₦{bundle.compare_at_price.toLocaleString('en-NG')}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleBundleClick(bundle)}
                      className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
                    >
                      Enroll in bundle
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-slate-900 py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-4 text-3xl font-bold text-slate-50">
            Not sure which program is right for you?
          </h2>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-slate-300">
            Our admissions team is here to help you choose the perfect pathway based on your experience,
            goals, and schedule.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center rounded-lg bg-blue-600 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-700"
            >
              Book a Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="tel:+2349058681595"
              className="inline-flex items-center rounded-lg border border-slate-600 px-6 py-3 font-semibold text-slate-200 transition-colors hover:bg-slate-800"
            >
              Call Us Today
            </a>
          </div>
        </div>
      </section>

      {(selectedCourse || selectedBundle) && (
        <EnrollmentModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedCourse(null);
            setSelectedBundle(null);
          }}
          courseId={selectedCourse?.id}
          courseTitle={selectedCourse?.title}
          coursePrice={selectedCourse?.price}
          priceValue={selectedCourse?.priceValue}
          bundle={
            selectedBundle
              ? {
                  id: selectedBundle.id,
                  title: selectedBundle.title,
                  price: `₦${selectedBundle.price_value.toLocaleString('en-NG')}`,
                  priceValue: selectedBundle.price_value,
                  courseIds: selectedBundle.course_ids,
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default CoursesPage;
